import { ArtworkPrintSettings } from "@/components/artwork/artworkPrintSettings";
import { MultiplePrintSettings } from "@/components/multipleprint/multipleprintPrintSettings";
import { PhotographyPrintSettings } from "@/components/photography/photographyPrintSettings";
import { SupportedLocale } from "@/i18n";
import api from "@/lib/axiosConfig";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";

/** Inline (unsaved) cover page — rendered when no coverPageId is selected. */
type InlineCoverPageParams = {
  title?: string;
  paragraph1?: string;
  paragraph2?: string;
  imageLink?: string | null;
  imageSize?: "large" | "small";
  imagePosition?: "header" | "after_title" | "after_para1" | "footer";
};

type GenerateArtworksPdfParams = {
  lang: string;
  template: string;
  settings: ArtworkPrintSettings;
  artworkIds: string[];
  orderBy: object[];
  selectedAttachmentIds?: string[];
  coverPageId?: string;
  coverPage?: InlineCoverPageParams;
  // Page layout options
  itemsPerPage?: string;
  showTitle?: boolean;
  showPagination?: boolean;
  showPrintDate?: boolean;
  listTitle?: string;
  binderImageSize?: string;
  textAlignment?: string;
  headerAlignment?: string;
  imageAlignment?: string;
  format?: "pdf" | "word";
};

type GenerateArtworkSinglePdfParams = {
  lang: string;
  template: string;
  settings: ArtworkPrintSettings;
  artworkId: string;
  selectedAttachmentIds?: string[];
  coverPageId?: string;
  coverPage?: InlineCoverPageParams;
  // Page layout options
  itemsPerPage?: string;
  showTitle?: boolean;
  showPagination?: boolean;
  showPrintDate?: boolean;
  listTitle?: string;
  binderImageSize?: string;
  textAlignment?: string;
  headerAlignment?: string;
  imageAlignment?: string;
  format?: "pdf" | "word";
};

// List Text PDF generation params (single endpoint for 1 or N artworks)
type GenerateListTextPdfParams = {
  lang: string;
  artworkIds: string[];
  orderBy?: object[];
  showThumbnails: boolean;
  /** Champs à afficher dans le résumé court (défaut : tous). */
  summaryFields?: {
    registrationNumber?: boolean;
    artist?: boolean;
    title?: boolean;
    creationDate?: boolean;
    dimensions?: boolean;
    techniqueType?: boolean;
    signature?: boolean;
    status?: boolean;
    price?: boolean;
    storage?: boolean;
    hideInches?: boolean;
    hideCentimeters?: boolean;
    hideRegistrationLabel?: boolean;
  };
  /** Ordre d'affichage des champs du résumé (clés). */
  summaryFieldsOrder?: string[];
  format?: "pdf" | "word";
  // Page layout options
  showTitle?: boolean;
  showPagination?: boolean;
  showPrintDate?: boolean;
  textAlignment?: "left" | "center" | "right";
  /** Custom list title (bold header) */
  listTitle?: string;
  /** Custom list subtitle */
  listSubtitle?: string;
};

export const baseRoute = "back/api/pdf";

export const administrativeDocumentTypeSchema = z.enum([
  "document1",
  "document2",
  "document3",
]);

export type AdministrativeDocumentType = z.infer<
  typeof administrativeDocumentTypeSchema
>;

export const templateStyleSchema = z.enum(["standard", "centered"]);

export type TemplateStyle = z.infer<typeof templateStyleSchema>;

export const generateAdministrativeDocumentSchema = z.object({
  hideDate: z.boolean().optional(),
  oeuvreExaminee: z.boolean(),
  signloca: z.string(),
  documentType: administrativeDocumentTypeSchema,
  templateStyle: templateStyleSchema.optional(),
  showLogo: z.boolean().optional(),
  documentNumber: z.string().optional(),
  additionalParagraph: z.string().optional(),
  showInscriptionsDos: z.boolean().optional(),
  showImageDos: z.boolean().optional(),
  selectedAttachmentIds: z.array(z.string()).optional(),
});

export type GenerateAdministrativeDocument = z.infer<
  typeof generateAdministrativeDocumentSchema
>;

export type GeneratedPdf = {
  file: File;
  /** Download file name parsed from the API Content-Disposition header, if any. */
  filename?: string;
};

const parseFilenameFromHeaders = (headers: {
  [key: string]: unknown;
}): string | undefined => {
  const cd = (headers["content-disposition"] as string) ?? "";
  const match = /filename="?([^";]+)"?/i.exec(cd);
  return match?.[1];
};

const generateSingleArtworksPdf = async (
  data: GenerateArtworkSinglePdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/artwork-single", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGenerateSingleArtworksPdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GenerateArtworkSinglePdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateSingleArtworksPdf,
  });

const generateArtworksPdf = async (
  data: GenerateArtworksPdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/artworks", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGenerateArtworksPdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GenerateArtworksPdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateArtworksPdf,
  });

// List Text PDF generation (single endpoint for 1 or N artworks)
const generateListTextPdf = async (
  data: GenerateListTextPdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/list-text", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGenerateListTextPdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GenerateListTextPdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateListTextPdf,
  });

/**
 * List Text « full format » (entityType + ids + settings) — utilisé pour le
 * modèle « Liste de multiples – Vignettes » (entityType "multipleprint").
 * Le backend supporte déjà multipleprint (summaries + vignettes + thumbnails).
 */
export type ListTextSummaryFields = {
  registrationNumber?: boolean;
  artist?: boolean;
  title?: boolean;
  creationDate?: boolean;
  dimensions?: boolean;
  techniqueType?: boolean;
  signature?: boolean;
  status?: boolean;
};

type GenerateListTextPrintParams = {
  entityType: "multipleprint";
  ids: string[];
  lang: string;
  format?: ExportFormat;
  settings: {
    showThumbnails: boolean;
    summaryFields?: ListTextSummaryFields;
    showTitle?: boolean;
    showPagination?: boolean;
    showPrintDate?: boolean;
    textAlignment?: "left" | "center" | "right";
    listTitle?: string;
  };
};

const generateListTextPrint = async (
  data: GenerateListTextPrintParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/list-text", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGenerateListTextPrint = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GenerateListTextPrintParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateListTextPrint,
  });

type GeneratePhotographiesPdfParams = {
  lang: string;
  template: string;
  settings: PhotographyPrintSettings;
  photographyIds: string[];
  orderBy: object[];
};

const generatePhotographiesPdf = async (
  data: GeneratePhotographiesPdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/photographies", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGeneratePhotographiesPdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GeneratePhotographiesPdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generatePhotographiesPdf,
  });

type GeneratePhotographiesSinglePdfParams = {
  lang: string;
  template: string;
  settings: PhotographyPrintSettings;
  photographyId: string;
};

const generatePhotographiesSinglePdf = async (
  data: GeneratePhotographiesSinglePdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/photography-single", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGeneratePhotographiesSinglePdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GeneratePhotographiesSinglePdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generatePhotographiesSinglePdf,
  });

type GenerateMultipleprintSinglePdfParams = {
  lang: string;
  template: string;
  settings: MultiplePrintSettings;
  multipleprintId: string;
  format?: ExportFormat;
  selectedAttachmentIds?: string[];
  coverPageId?: string;
  coverPage?: InlineCoverPageParams;
  // Page layout options
  itemsPerPage?: string;
  showTitle?: boolean;
  showPagination?: boolean;
  showPrintDate?: boolean;
  listTitle?: string;
  binderImageSize?: string;
  textAlignment?: string;
  headerAlignment?: string;
  imageAlignment?: string;
};

const generateMultipleprintSinglePdf = async (
  data: GenerateMultipleprintSinglePdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/multipleprint-single", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGenerateMultipleprintSinglePdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GenerateMultipleprintSinglePdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateMultipleprintSinglePdf,
  });

// Fiche d'UN exemplaire d'estampe multiple : mêmes options que la fiche
// multiple, mais ciblée sur un Multiplesample.
type GenerateMultiplesampleSinglePdfParams = Omit<
  GenerateMultipleprintSinglePdfParams,
  "multipleprintId"
> & {
  multiplesampleId: string;
};

const generateMultiplesampleSinglePdf = async (
  data: GenerateMultiplesampleSinglePdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/multiplesample-single", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGenerateMultiplesampleSinglePdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GenerateMultiplesampleSinglePdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateMultiplesampleSinglePdf,
  });

type GenerateMultipleprintsPdfParams = {
  lang: string;
  template: string;
  settings: MultiplePrintSettings;
  multipleprintIds: string[];
  format?: ExportFormat;
  coverPageId?: string;
  coverPage?: InlineCoverPageParams;
  selectedAttachmentIds?: string[];
  itemsPerPage?: string;
  showPagination?: boolean;
  showPrintDate?: boolean;
  listTitle?: string;
  binderImageSize?: string;
  textAlignment?: string;
  headerAlignment?: string;
  imageAlignment?: string;
};

const generateMultipleprintsPdf = async (
  data: GenerateMultipleprintsPdfParams,
): Promise<GeneratedPdf> => {
  const res = await api.post<File>("back/api/pdf/multipleprints", data, {
    responseType: "blob",
  });
  return { file: res.data, filename: parseFilenameFromHeaders(res.headers) };
};

export const useGenerateMultipleprintsPdf = (
  options?: Omit<
    UseMutationOptions<GeneratedPdf, Error, GenerateMultipleprintsPdfParams>,
    "mutationFn"
  >,
) =>
  useMutation({
    ...options,
    mutationFn: generateMultipleprintsPdf,
  });

export type ExportFormat = "pdf" | "word";

const generateAdministrativeDocument = async ({
  params,
  artworkId,
  lang,
  format = "pdf",
}: {
  params: GenerateAdministrativeDocument;
  artworkId: string;
  lang: SupportedLocale;
  format?: ExportFormat;
}) => {
  const { data } = await api.post<File>(
    `${baseRoute}/administrative-document/${artworkId}`,
    { ...params, format },
    { params: { lang }, responseType: "blob" },
  );
  return data;
};

export const useGenerateAdministrativeDocumentsPdf = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateAdministrativeDocument,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({
        queryKey: ["artworks", v.artworkId, "administrative-documents"],
      });
    },
  });
};

// Certificat d'un exemplaire d'estampe multiple (document administratif).
const generateMultiplesampleAdministrativeDocument = async ({
  params,
  multiplesampleId,
  lang,
  format = "pdf",
}: {
  params: GenerateAdministrativeDocument;
  multiplesampleId: string;
  lang: SupportedLocale;
  format?: ExportFormat;
}) => {
  const { data } = await api.post<File>(
    `${baseRoute}/multiplesample-administrative-document/${multiplesampleId}`,
    { ...params, format },
    { params: { lang }, responseType: "blob" },
  );
  return data;
};

export const useGenerateMultiplesampleAdministrativeDocumentPdf = () =>
  useMutation({ mutationFn: generateMultiplesampleAdministrativeDocument });

// Certificate Logo API hooks

type CertificateLogoResponse = {
  certificateLogo: string | null;
  logoUrl: string | null;
  certificateLogoAlt: string | null;
};

type UploadCertificateLogoResponse = {
  certificateLogo: string;
  certificateLogoAlt: string | null;
  certificateDefaultTemplate: string;
};

export function useGetCertificateLogo() {
  return useQuery({
    queryKey: ["certificate-logo"],
    queryFn: async () => {
      const { data } = await api.get<CertificateLogoResponse>(
        "back/api/pdf/certificate-logo",
      );
      return data;
    },
  });
}

export function useUploadCertificateLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("logo", file);
      const { data } = await api.patch<UploadCertificateLogoResponse>(
        "back/api/pdf/certificate-logo",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificate-logo"] });
    },
  });
}

export function useDeleteCertificateLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete<{ success: boolean }>(
        "back/api/pdf/certificate-logo",
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["certificate-logo"] });
    },
  });
}
